import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { buildWaLink, imgSrc, SUBS } from "../lib/api";
import { Lightbox } from "./Lightbox";

const TABS = [
    ["todos", "TODOS"],
    ["futebol", "FUTEBOL"],
    ["retro", "RETRÔ"],
    ["basquete", "BASQUETE"],
];

const EASE = [0.16, 1, 0.3, 1];

export const Catalog = ({ products, category, setCategory, whatsapp }) => {
    const [activeIndex, setActiveIndex] = useState(null);
    const [sub, setSub] = useState("todas");

    const filtered = useMemo(() => {
        let list =
            category === "todos" ? products : products.filter((p) => p.category === category);
        if (category === "futebol" && sub !== "todas") {
            list = list.filter((p) => p.subcategory === sub);
        }
        return list;
    }, [products, category, sub]);

    const active = activeIndex !== null ? filtered[activeIndex] : null;

    const selectCategory = (id) => {
        setCategory(id);
        setSub("todas");
        setActiveIndex(null);
    };

    return (
        <section
            id="catalogo"
            data-testid="catalog-section"
            className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 scroll-mt-16"
        >
            <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <p className="mono-label">// Catálogo</p>
                    <h2 className="mt-3 font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tight">
                        Peças em destaque
                    </h2>
                </div>
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA]">
                    Toque na imagem para ampliar // Pedido via WhatsApp
                </p>
            </div>

            <div data-testid="category-tabs" className="mt-10 flex gap-2 overflow-x-auto pb-1">
                {TABS.map(([id, label]) => (
                    <button
                        key={id}
                        data-testid={`catalog-tab-${id}`}
                        onClick={() => selectCategory(id)}
                        className={`shrink-0 border px-5 py-2.5 font-condensed font-bold uppercase tracking-widest text-sm transition-colors duration-300 ${
                            category === id
                                ? "border-[#FF6A00] bg-[#FF6A00] text-black"
                                : "border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-white/40"
                        }`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {category === "futebol" && (
                <div
                    data-testid="subcategory-tabs"
                    className="mt-4 flex gap-2 overflow-x-auto pb-1"
                >
                    {[["todas", "TODOS"], ...SUBS].map(([id, label]) => (
                        <button
                            key={id}
                            data-testid={`sub-tab-${id}`}
                            onClick={() => {
                                setSub(id);
                                setActiveIndex(null);
                            }}
                            className={`shrink-0 border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                                sub === id
                                    ? "border-[#FF6A00] text-[#FF6A00]"
                                    : "border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-white/40"
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            )}

            {filtered.length === 0 ? (
                <div
                    data-testid="empty-state"
                    className="mt-10 border border-dashed border-[#27272A] py-24 text-center px-6"
                >
                    <p className="mono-label">Catálogo em atualização</p>
                    <p className="mt-3 text-sm text-[#A1A1AA]">
                        Novas peças desta categoria em breve.
                    </p>
                </div>
            ) : (
                <motion.div
                    layout
                    data-testid="catalog-grid"
                    className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5"
                >
                    <AnimatePresence mode="popLayout">
                        {filtered.map((product, i) => (
                            <motion.button
                                layout
                                key={product.id}
                                data-testid={`product-card-${product.id}`}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-40px" }}
                                exit={{ opacity: 0, scale: 0.96 }}
                                transition={{ duration: 0.5, ease: EASE }}
                                onClick={() => setActiveIndex(i)}
                                className="group relative aspect-[3/4] overflow-hidden border border-[#27272A] bg-[#141414] transition-colors duration-300 hover:border-[#FF6A00]"
                            >
                                <img
                                    src={imgSrc(product.image_url)}
                                    alt=""
                                    loading="lazy"
                                    data-testid={`product-image-${product.id}`}
                                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                                />
                                {whatsapp && (
                                    <a
                                        data-testid={`whatsapp-quick-${product.id}`}
                                        href={buildWaLink(whatsapp, product.ref)}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        aria-label="Pedir pelo WhatsApp"
                                        className="absolute bottom-3 right-3 flex h-10 w-10 items-center justify-center bg-[#FF6A00] text-black opacity-0 transition-opacity duration-300 group-hover:opacity-100 hover:bg-[#FF8533]"
                                    >
                                        <MessageCircle size={18} strokeWidth={2.5} />
                                    </a>
                                )}
                            </motion.button>
                        ))}
                    </AnimatePresence>
                </motion.div>
            )}

            <AnimatePresence>
                {active && (
                    <Lightbox
                        product={active}
                        whatsapp={whatsapp}
                        onClose={() => setActiveIndex(null)}
                        onPrev={() =>
                            setActiveIndex((i) => (i - 1 + filtered.length) % filtered.length)
                        }
                        onNext={() => setActiveIndex((i) => (i + 1) % filtered.length)}
                    />
                )}
            </AnimatePresence>
        </section>
    );
};
